/**
 * RiskAssessmentController orchestrates the route risk assessment in the Route Assessment tab: it reads the
 * route currently shown by the global DirectionsRenderer, collects the optional inputs from
 * #riskAssessmentForm, calls POST /routing/assess and hands the result to the panel
 * (RiskAssessmentPanel) and the map overlay (RiskSegmentOverlay).
 *
 * Created by Map.create() right after the SafetyRouteController (so window.directionsRenderer exists)
 * and exposed on the Map instance so MapController (the AI-assistant facade) can trigger it.
 *
 * Invalidation: a changed route clears the result and asks for a re-run; changed inputs only flag the
 * result as stale. Responses are matched against the polyline they were requested for, so a late
 * response for an old route is dropped.
 *
 * Geometry: the engine receives the detailed step path of the route (not Google's simplified
 * overview polyline), and the overlay is drawn on that same path — see _detailedPathOf().
 */

/* global google */

import axios from "axios";
import { RiskAssessmentPanel } from "./RiskAssessmentPanel";
import { RiskSegmentOverlay } from "./RiskSegmentOverlay";

const MAX_WAYPOINTS = 25;
const MAX_STEPS = 800;
const REQUEST_TIMEOUT_MS = 120000;
// Report generation runs server-side in the background; the panel polls the status endpoint.
const REPORT_POLL_INTERVAL_MS = 3000;
const REPORT_POLL_MAX_MS = 450000;

export class RiskAssessmentController {
  /**
   * @param {import("../Map").Map} map
   */
  constructor(map) {
    this.form = document.getElementById("riskAssessmentForm");
    if (!this.form) {
      return;
    }
    this.map = map;
    this.apiUrls = map.apiUrls || {};
    this.button = document.getElementById("assessRoute");
    this.hint = document.getElementById("rraHint");
    // The assessment owns its corridor control and mirrors the resilience incident radius.
    // The server uses this half-width for both incidents and secure parking.
    this.corridorSlider = document.getElementById("rraCorridorKm");
    this.panel = new RiskAssessmentPanel(document.getElementById("riskAssessmentPanel"), { assistantState: map.assistantState });
    this.overlay = new RiskSegmentOverlay(map.map);

    this.current = null;
    this.assessedPolyline = null;
    this.sequence = 0;
    this.reportPoll = null;
    this.buttonHtml = this.button ? this.button.innerHTML : "";

    axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
    axios.defaults.headers.common["Content-Type"] = "application/json";
    if (this.apiUrls.csrfToken) {
      axios.defaults.headers.common["X-CSRF-Token"] = this.apiUrls.csrfToken;
    }

    if (this.button) {
      this.button.addEventListener("click", (event) => {
        event.preventDefault();
        this.assess();
      });
    }
    this.form.addEventListener("change", () => this._onInputsChanged());
    this._setDefaultDepartureTime();
    this._bindCorridorSliders();

    if (window.directionsRenderer && typeof window.directionsRenderer.addListener === "function") {
      window.directionsRenderer.addListener("directions_changed", () => this._onDirectionsChanged());
    }
    this._onDirectionsChanged();
  }

  _bindCorridorSliders() {
    const assessment = this.corridorSlider;
    if (!assessment) {
      return;
    }
    const resilience = document.querySelector("#safetyFilterForm input[name=dangerRadius]");
    const assessmentLabel = document.getElementById("rraCorridorKmValue");
    const resilienceLabel = document.getElementById("dangerRadiusValue");
    const sync = (source) => {
      assessment.value = source.value;
      if (resilience) {
        resilience.value = source.value;
      }
      [assessmentLabel, resilienceLabel].forEach((label) => {
        if (label) {
          label.textContent = `${source.value} km`;
        }
      });
    };
    sync(resilience || assessment);
    [assessment, resilience].filter(Boolean).forEach((slider) => {
      ["input", "change"].forEach((type) => {
        slider.addEventListener(type, () => {
          sync(slider);
          this._onInputsChanged();
          if (slider === assessment && resilience && type === "change") {
            resilience.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      });
    });
  }

  /**
   * The route currently displayed by the primary renderer, or null.
   * @returns {google.maps.DirectionsRoute|null}
   */
  _activeRoute() {
    try {
      const renderer = window.directionsRenderer;
      if (!renderer) {
        return null;
      }
      const d = renderer.getDirections();
      const i = renderer.getRouteIndex() || 0;
      return (d && d.routes && d.routes[i]) || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * The detailed path of a route: the step paths of all legs concatenated — the geometry the
   * DirectionsRenderer draws. Google's overview_path is simplified and cuts curves, so anything
   * measured on it (segment km, mandatory-stop positions, the overlay) falls short of the drawn
   * route. Falls back to a step's encoded polyline and, for routes without step geometry, to
   * overview_path.
   *
   * @param {google.maps.DirectionsRoute|null} route
   * @returns {google.maps.LatLng[]|null} at least two points, or null
   */
  _detailedPathOf(route) {
    if (!route) {
      return null;
    }
    const path = [];
    const push = (point) => {
      if (!point) {
        return;
      }
      const last = path[path.length - 1];
      if (!last || !last.equals(point)) {
        path.push(point);
      }
    };
    (route.legs || []).forEach((leg) => {
      (leg.steps || []).forEach((step) => {
        let points = Array.isArray(step.path) && step.path.length ? step.path : null;
        if (!points && typeof step.encoded_lat_lngs === "string" && step.encoded_lat_lngs) {
          try {
            points = google.maps.geometry.encoding.decodePath(step.encoded_lat_lngs);
          } catch (e) {
            points = null;
          }
        }
        (points || []).forEach(push);
      });
    });
    if (path.length >= 2) {
      return path;
    }
    const overview = route.overview_path;
    return Array.isArray(overview) && overview.length >= 2 ? overview : null;
  }

  /**
   * Path + encoded polyline of a route, cached per route object: this runs on every
   * directions_changed and on every staleness check, and encoding ~24 points per km is not free.
   *
   * @param {google.maps.DirectionsRoute|null} route
   * @returns {{route: google.maps.DirectionsRoute, path: google.maps.LatLng[]|null, polyline: string|null}|null}
   */
  _geometryOf(route) {
    if (!route) {
      return null;
    }
    if (this._geometryCache && this._geometryCache.route === route) {
      return this._geometryCache;
    }
    const path = this._detailedPathOf(route);
    let polyline = null;
    if (path) {
      try {
        polyline = google.maps.geometry.encoding.encodePath(path) || null;
      } catch (e) {
        polyline = null;
      }
    }
    this._geometryCache = { route, path, polyline };
    return this._geometryCache;
  }

  /**
   * Encoded detailed polyline of a route (what is sent to the engine and used to match
   * responses against the live route), or null when the route has no usable geometry.
   *
   * @param {google.maps.DirectionsRoute|null} route
   * @returns {string|null}
   */
  _polylineOf(route) {
    const geometry = this._geometryOf(route);
    return geometry ? geometry.polyline : null;
  }

  /**
   * @param {google.maps.DirectionsRoute|null} route
   * @returns {google.maps.LatLng[]|null}
   */
  _pathOf(route) {
    const geometry = this._geometryOf(route);
    return geometry ? geometry.path : null;
  }

  /**
   * Builds the /routing/assess request body for a route plus the current form inputs.
   *
   * @param {google.maps.DirectionsRoute} route
   * @returns {object}
   */
  buildPayload(route) {
    const legs = (route.legs || []).map((leg) => ({
      distanceM: leg.distance ? leg.distance.value : 0,
      durationS: leg.duration ? leg.duration.value : 0,
      startLat: leg.start_location.lat(),
      startLng: leg.start_location.lng(),
      endLat: leg.end_location.lat(),
      endLng: leg.end_location.lng(),
    }));
    const firstLeg = route.legs && route.legs[0];
    const lastLeg = route.legs && route.legs[route.legs.length - 1];

    // Google steps in route order: distance, duration and the instruction text feed the
    // server-side road-class heuristic (motorway vs. other road).
    const steps = [];
    (route.legs || []).forEach((leg) => {
      (leg.steps || []).forEach((step) => {
        steps.push({
          distanceM: step.distance ? step.distance.value : 0,
          durationS: step.duration ? step.duration.value : 0,
          instruction: typeof step.instructions === "string" ? step.instructions.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 300) : "",
        });
      });
    });

    const waypoints = [];
    (route.legs || []).forEach((leg, index) => {
      (leg.via_waypoints || []).forEach((point) => {
        waypoints.push({ lat: point.lat(), lng: point.lng(), name: null });
      });
      if (index < route.legs.length - 1) {
        waypoints.push({ lat: leg.end_location.lat(), lng: leg.end_location.lng(), name: leg.end_address || null });
      }
    });

    const number = (id) => {
      const el = document.getElementById(id);
      const value = el && el.value !== "" ? Number(el.value) : NaN;
      return Number.isFinite(value) ? value : null;
    };
    const highValue = document.getElementById("rraHighValue");
    const dataWindow = document.getElementById("rraDataWindow");
    const departure = this._setDefaultDepartureTime();

    // The datetime-local value is the wall clock of the planner; it is sent as ISO-8601 with the
    // browser's UTC offset so the engine can derive day/night along the route.
    let departureTime = null;
    if (departure && departure.value) {
      const date = new Date(departure.value);
      departureTime = Number.isNaN(date.getTime()) ? null : this._toIsoWithOffset(date);
    }

    return {
      polyline: this._polylineOf(route),
      legs,
      origin: firstLeg
        ? { lat: firstLeg.start_location.lat(), lng: firstLeg.start_location.lng(), name: firstLeg.start_address || null }
        : null,
      destination: lastLeg
        ? { lat: lastLeg.end_location.lat(), lng: lastLeg.end_location.lng(), name: lastLeg.end_address || null }
        : null,
      waypoints: waypoints.slice(0, MAX_WAYPOINTS),
      corridorKm: this.corridorSlider ? Number(this.corridorSlider.value) || null : null,
      cargo: { highValue: highValue && highValue.checked ? true : null },
      dataWindowMonths: dataWindow && dataWindow.value !== "" ? Number(dataWindow.value) || null : null,
      departureTime,
      steps: steps.slice(0, MAX_STEPS),
      driverHours: {
        accumulatedDailyH: number("rraDailyH"),
        continuousSinceBreakH: number("rraContinuousH"),
      },
    };
  }

  /**
   * Runs the assessment for the route currently on the map. Options (from the assistant facade) are
   * copied into the form controls first so the UI reflects what was assessed.
   *
   * @param {{highValue?: boolean, dataWindowMonths?: number|null, departureTime?: string|null, driverHours?: {accumulatedDailyH?: number|null, continuousSinceBreakH?: number|null}}} [options]
   * @returns {Promise<boolean>}
   */
  async assess(options = {}) {
    if (!this.form) {
      return false;
    }
    if (options && Object.keys(options).length) {
      this._applyOptions(options);
    }
    const tab = document.getElementById("nav-assessment-tab");
    if (tab) {
      tab.click();
    }

    const route = this._activeRoute();
    if (!route || !this._polylineOf(route)) {
      this.panel.error("Plan a route first.");
      return false;
    }

    const payload = this.buildPayload(route);
    const sequence = ++this.sequence;
    this._setBusy(true);
    this.panel.loading("Assessing route…");

    try {
      const response = await axios.post(this.apiUrls.assess, payload, { timeout: REQUEST_TIMEOUT_MS });
      const data = response.data;
      if (sequence !== this.sequence) {
        return false; // a newer request superseded this one
      }
      const liveRoute = this._activeRoute();
      if (this._polylineOf(liveRoute) !== payload.polyline) {
        this.panel.message("Route changed — run the assessment again.", "alert alert-light small");
        return false;
      }
      if (!data || data.ok === false || !data.kpi) {
        this.panel.error((data && data.error) || "The assessment failed.");
        return false;
      }

      this._stopReportPolling();
      this.current = data;
      this.assessedPolyline = payload.polyline;
      this.panel.render(data, {
        onHover: (id) => this.overlay.highlight(id),
        onSelect: (id) => this.overlay.highlight(id),
        onGenerateReport: () => this.generateReport(),
        onConsentRequired: () => this._openConsentModal(),
      });
      // Draw on the same detailed path the engine measured, so segments and stop markers
      // coincide with the rendered route.
      this.overlay.render(data.kpi, this._pathOf(liveRoute), data.bands, {
        onSelect: (id) => this.panel.expandSegment(id),
      });
      // A cached assessment may already carry a running or finished report.
      if (data.report && data.report.status === "pending") {
        this._startReportPolling(data.assessmentId);
      }
      return true;
    } catch (error) {
      if (sequence === this.sequence) {
        this.panel.error(this._errorMessage(error));
      }
      return false;
    } finally {
      if (sequence === this.sequence) {
        this._setBusy(false);
      }
    }
  }

  _errorMessage(error) {
    const response = error && error.response;
    const serverMessage = response && response.data && response.data.error;
    if (serverMessage) {
      return serverMessage;
    }
    if (error && (error.code === "ECONNABORTED" || /timeout/i.test(error.message || ""))) {
      return "The assessment took too long. Try a shorter route or a narrower corridor.";
    }
    switch (response && response.status) {
      case 401:
        return "Please log in to assess routes.";
      case 403:
        return "Resilience features are not enabled for your account.";
      case 422:
        return "The assessment request was rejected as invalid input.";
      case 404:
        return "The assessment endpoint is not available.";
      default:
        return "The assessment failed. Please try again.";
    }
  }

  _setDefaultDepartureTime() {
    const departure = document.getElementById("rraDepartureTime");
    if (departure && !departure.value) {
      departure.value = this._toLocalDateTimeValue(new Date());
    }
    return departure;
  }

  _applyOptions(options) {
    const highValue = document.getElementById("rraHighValue");
    if (highValue && options.highValue !== undefined) {
      highValue.checked = !!options.highValue;
    }
    const dataWindow = document.getElementById("rraDataWindow");
    if (dataWindow && options.dataWindowMonths !== undefined) {
      const value = options.dataWindowMonths === null ? "" : String(options.dataWindowMonths);
      dataWindow.value = [...dataWindow.options].some((o) => o.value === value) ? value : "";
    }
    const departure = document.getElementById("rraDepartureTime");
    if (departure && options.departureTime !== undefined) {
      departure.value = options.departureTime ? this._toLocalDateTimeValue(options.departureTime) : "";
    }
    const hours = options.driverHours || {};
    const daily = document.getElementById("rraDailyH");
    if (daily && hours.accumulatedDailyH !== undefined) {
      daily.value = hours.accumulatedDailyH === null ? "" : hours.accumulatedDailyH;
    }
    const continuous = document.getElementById("rraContinuousH");
    if (continuous && hours.continuousSinceBreakH !== undefined) {
      continuous.value = hours.continuousSinceBreakH === null ? "" : hours.continuousSinceBreakH;
    }
  }

  /**
   * Local Date → "YYYY-MM-DDTHH:mm:00+HH:MM" (ISO-8601 with the browser's UTC offset).
   *
   * @param {Date} date
   * @returns {string}
   */
  _toIsoWithOffset(date) {
    const pad = (n) => String(Math.abs(n)).padStart(2, "0");
    const offsetMin = -date.getTimezoneOffset();
    const sign = offsetMin >= 0 ? "+" : "-";
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00` +
      `${sign}${pad(Math.floor(Math.abs(offsetMin) / 60))}:${pad(Math.abs(offsetMin) % 60)}`
    );
  }

  /**
   * ISO/RFC date string → "YYYY-MM-DDTHH:mm" in local time for a datetime-local input.
   */
  _toLocalDateTimeValue(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  _setBusy(busy) {
    if (!this.button) {
      return;
    }
    if (busy) {
      this.button.disabled = true;
      this.button.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Assessing…`;
    } else {
      this.button.innerHTML = this.buttonHtml;
      this.button.disabled = !this._activeRoute();
    }
  }

  _onDirectionsChanged() {
    const route = this._activeRoute();
    const polyline = this._polylineOf(route);
    if (this.button) {
      this.button.disabled = !polyline;
    }
    if (this.hint) {
      this.hint.classList.toggle("d-none", !!polyline);
    }
    if (this.current && polyline !== this.assessedPolyline) {
      this.clear();
      this.panel.message("Route changed — run the assessment again.", "alert alert-light small");
    }
  }

  _onInputsChanged() {
    if (this.current) {
      this.panel.markStale();
    }
  }

  /**
   * Starts the LLM report for the current assessment (concept §4 step 7). The POST runs the
   * generation server-side and is deliberately not awaited for its result — the status endpoint
   * is polled instead, so a long generation survives proxy timeouts and tab switches. A 4xx on
   * the POST (consent, budget, expired assessment) is surfaced immediately.
   *
   * @returns {boolean} whether a generation was requested
   */
  generateReport() {
    if (!this.current || !this.current.assessmentId || !this.apiUrls.assessmentReport) {
      return false;
    }
    const assessmentId = this.current.assessmentId;
    if (this.current.report && this.current.report.status === "pending") {
      return false;
    }
    const previousReport = this.current.report;
    const poll = this._startReportPolling(assessmentId, previousReport);
    this._applyReportState({ status: "pending", timeoutSeconds: previousReport && previousReport.timeoutSeconds });

    axios
      .post(this.apiUrls.assessmentReport, { assessmentId }, { timeout: poll.timeoutMs })
      .then((response) => {
        const data = response && response.data;
        if (this._isReportPollCurrent(poll) && data && data.report) {
          poll.awaitingClaim = false;
          this._applyReportState(data.report);
          if (["done", "error"].includes(data.report.status)) {
            this._stopReportPolling();
          }
        }
      })
      .catch((error) => {
        if (!this._isReportPollCurrent(poll)) {
          return;
        }
        const response = error && error.response;
        // Timeouts / connection drops: the server keeps generating; polling reports the outcome.
        if (!response || response.status >= 500 || response.status === 408) {
          return;
        }
        this._stopReportPolling();
        const reason = response.data && response.data.reason;
        if (response.status === 403 && reason === "assistant_consent") {
          this._applyReportState(null);
          this._openConsentModal();
          return;
        }
        this._applyReportState({ status: "error", error: this._reportErrorMessage(error) });
      });
    return true;
  }

  _reportErrorMessage(error) {
    const response = error && error.response;
    const serverMessage = response && response.data && response.data.error;
    if (serverMessage) {
      return serverMessage;
    }
    switch (response && response.status) {
      case 401:
        return "Please log in to generate reports.";
      case 403:
        return "Report generation is not available for your account.";
      case 404:
        return "The assessment has expired — run it again.";
      case 429:
        return "The AI budget for this month is exhausted.";
      default:
        return "Report generation failed. Please try again.";
    }
  }

  _startReportPolling(assessmentId, previousReport) {
    this._stopReportPolling();
    const report = previousReport || (this.current && this.current.report);
    const timeoutSeconds = Number(report && report.timeoutSeconds);
    const timeoutMs = timeoutSeconds > 0 ? (timeoutSeconds + 30) * 1000 : REPORT_POLL_MAX_MS;
    const poll = {
      assessmentId, timeoutMs, timer: null, deadline: null, request: null,
      awaitingClaim: previousReport !== undefined,
      previousStartedAt: previousReport && previousReport.startedAt,
    };
    this.reportPoll = poll;
    poll.deadline = window.setTimeout(() => {
      if (this._isReportPollCurrent(poll)) {
        this._stopReportPolling();
        this._applyReportState({ status: "error", error: "Report generation timed out. Please try again." });
      }
    }, timeoutMs);
    const tick = async () => {
      if (!this._isReportPollCurrent(poll)) {
        return;
      }
      try {
        poll.request = new AbortController();
        const response = await axios.get(this.apiUrls.assessmentReportStatus, {
          params: { id: assessmentId }, timeout: 15000, signal: poll.request.signal,
        });
        const data = response && response.data;
        if (!this._isReportPollCurrent(poll)) {
          return;
        }
        // A poll can arrive before the POST has claimed the row (including during regeneration).
        if (data && data.report && data.report.status) {
          if (data.report.status === "pending" || data.report.startedAt !== poll.previousStartedAt) {
            poll.awaitingClaim = false;
          }
          if (!poll.awaitingClaim) {
            this._applyReportState(data.report);
            if (["done", "error"].includes(data.report.status)) {
              this._stopReportPolling();
              return;
            }
          }
        }
      } catch (error) {
        if (!this._isReportPollCurrent(poll)) {
          return;
        }
        const response = error && error.response;
        if (response && response.status >= 400 && response.status < 500 && response.status !== 408) {
          this._stopReportPolling();
          this._applyReportState({ status: "error", error: this._reportErrorMessage(error) });
          return;
        }
        // transient network problem: keep polling
      }
      if (this._isReportPollCurrent(poll)) {
        poll.request = null;
        poll.timer = window.setTimeout(tick, REPORT_POLL_INTERVAL_MS);
      }
    };
    if (this.apiUrls.assessmentReportStatus) {
      poll.timer = window.setTimeout(tick, REPORT_POLL_INTERVAL_MS);
    }
    return poll;
  }

  _isReportPollCurrent(poll) {
    return this.reportPoll === poll && this.current && this.current.assessmentId === poll.assessmentId;
  }

  _stopReportPolling() {
    if (this.reportPoll) {
      window.clearTimeout(this.reportPoll.timer);
      window.clearTimeout(this.reportPoll.deadline);
      if (this.reportPoll.request) {
        this.reportPoll.request.abort();
      }
      this.reportPoll = null;
    }
  }

  _applyReportState(report) {
    if (this.current) {
      if (report && this.current.report) {
        // Keep the server's timeout and last known claim when a local network error creates
        // a retry state. The next attempt must still use the configured deadline and reject
        // terminal status responses belonging to the previous claim.
        report = {
          timeoutSeconds: this.current.report.timeoutSeconds,
          startedAt: this.current.report.startedAt,
          ...report,
        };
      }
      this.current.report = report;
    }
    this.panel.setReportState(report);
  }

  /**
   * Opens the assistant consent modal rendered by the layout for admin-enabled users without a
   * valid consent (frontend/views/layouts/_assistant_consent_modal.php).
   */
  _openConsentModal() {
    const jq = window.jQuery;
    const modal = document.getElementById("assistant-consent-modal");
    if (modal && jq && typeof jq.fn.modal === "function") {
      jq(modal).modal("show");
      return;
    }
    this.panel.setReportState({
      status: "error",
      error: "Enable your AI assistant on the account page and accept the privacy policy to generate reports.",
    });
  }

  /**
   * Removes the overlay and the panel content.
   * @returns {boolean}
   */
  clear() {
    if (!this.form) {
      return false;
    }
    this._stopReportPolling();
    this.overlay.clear();
    this.panel.clear();
    this.current = null;
    this.assessedPolyline = null;
    return true;
  }

  /**
   * Snapshot for the assistant facade.
   * @returns {{assessmentId: number, score: number, band: string, confidence: string, cached: boolean, report: object|null}|null}
   */
  getState() {
    if (!this.current || !this.current.kpi || !this.current.kpi.overallScore) {
      return null;
    }
    const overall = this.current.kpi.overallScore;
    const report = this.current.report || null;
    return {
      assessmentId: this.current.assessmentId,
      score: overall.value,
      band: overall.band,
      confidence: overall.confidence,
      cached: !!this.current.cached,
      report: report ? { status: report.status || null, pdfUrl: report.pdfUrl || null, error: report.error || null } : null,
    };
  }
}
